"use client";

import { useState, useEffect } from "react";
import {
  ArrowDownCircle,
  ArrowUpCircle,
  History,
  RefreshCw,
  DollarSign,
  Clock,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { Toaster, toast } from "react-hot-toast";
import { useAuth, useWallet } from "@crossmint/client-sdk-react-ui";
import { isBetaUser } from "../betaConfig";
import * as ethers from "ethers";
import { getFirestore, doc, getDoc, collection, query, where, orderBy, getDocs } from "firebase/firestore";
import { SiCashapp } from "react-icons/si";

const serverUrl = import.meta.env.VITE_SERVERURL;

interface VirtualAccountEvent {
  id: string;
  type: string;
  amount: string;
  currency: string;
  created_at: string;
  deposit_id?: string;
  destination_tx_hash?: string;
  developer_fee_amount: string;
  exchange_fee_amount: string;
  subtotal_amount: string;
  gas_fee: string;
  source: {
    payment_rail: string;
    description?: string;
    sender_name?: string;
    sender_bank_routing_number?: string;
    trace_number?: string;
    tracking_number?: string;
    clabe?: string;
    reference?: string;
  };
}

interface DrainEvent {
  id: string;
  amount: string;
  currency: string;
  state: string;
  created_at: string;
  deposit_tx_hash?: string;
  destination_tx_hash?: string;
  receipt: {
    initial_amount: string;
    developer_fee: string;
    subtotal_amount: string;
    converted_amount: string;
    outgoing_amount: string;
    destination_currency: string;
  };
}

type TabType = "deposits" | "withdrawals" | null;

interface LightningOrderRecord {
  id: string;
  orderId: string;
  usdAmount: number;
  btcAmount: string;
  status: string;
  createdAt: any;
}

export default function HistoryPage() {
  const { wallet } = useWallet();
  const { user } = useAuth();
  const [userWallet, setUserWallet] = useState<string>("");
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>(null);

  const [depositEvents, setDepositEvents] = useState<VirtualAccountEvent[]>([]);
  const [withdrawalDrains, setWithdrawalDrains] = useState<DrainEvent[]>([]);
  const [lightningOrders, setLightningOrders] = useState<LightningOrderRecord[]>([]);

  const [loading, setLoading] = useState(false);
  const [hasDeposits, setHasDeposits] = useState(false);
  const [hasWithdrawals, setHasWithdrawals] = useState(false);

  const db = getFirestore();

  // Get wallet address
  useEffect(() => {
    if (wallet?.address && ethers.isAddress(wallet.address)) {
      setUserWallet(wallet.address.toLowerCase());
    }
  }, [wallet]);

  // Load lightning orders from Firestore
  useEffect(() => {
    if (!userWallet) return;
    const fetchLightning = async () => {
      try {
        const q = query(
          collection(db, "lightningOrders"),
          where("walletAddress", "==", userWallet),
          orderBy("createdAt", "desc")
        );
        const snap = await getDocs(q);
        setLightningOrders(snap.docs.map((d) => ({ id: d.id, ...d.data() } as LightningOrderRecord)));
      } catch {}
    };
    fetchLightning();
  }, [userWallet, db]);

  // Load customer ID & detect available features
  useEffect(() => {
    if (!userWallet) return;

    const loadCustomer = async () => {
      try {
        const customerRef = doc(db, "bridge_customers-main", userWallet);
        const snap = await getDoc(customerRef);

        if (snap.exists()) {
          const cid = snap.data()?.customerId;
          if (cid) {
            setCustomerId(cid);
            await loadHistory(cid);
          }
        }
      } catch (err) {
        console.error("Failed to load customer ID", err);
      }
    };

    loadCustomer();
  }, [userWallet, db]);

  const loadHistory = async (cid: string) => {
    setLoading(true);

    try {
      // ──────────────────────────────────────────────
      //  Deposit history (virtual account events)
      // ──────────────────────────────────────────────
      const authH = { Authorization: `Bearer ${import.meta.env.VITE_API_SECRET_TOKEN}` };

      const vaRes = await fetch(
        `${serverUrl}/bridge/customers/${cid}/virtual_accounts`,
        { headers: authH },
      );
      if (vaRes.ok) {
        const vaData = await vaRes.json();
        if (vaData.data?.length > 0) {
          const va = vaData.data[0];
          if (va.status === "activated") {
            const histRes = await fetch(
              `${serverUrl}/bridge/customers/${cid}/virtual_accounts/${va.id}/history`,
              { headers: authH },
            );
            if (histRes.ok) {
              const hist = await histRes.json();
              setDepositEvents(hist.data || []);
              setHasDeposits(true);
              if (!activeTab) setActiveTab("deposits");
            }
          }
        }
      }

      // ──────────────────────────────────────────────
      //  Withdrawal history (liquidation addresses drains)
      // ──────────────────────────────────────────────
      const laRes = await fetch(
        `${serverUrl}/bridge/customers/${cid}/liquidation_addresses`,
        { headers: authH },
      );
      if (laRes.ok) {
        const laData = await laRes.json();
        if (laData.data?.length > 0) {
          // For simplicity — take the first active liquidation address
          const activeLA = laData.data.find((la: any) => la.state === "active");
          if (activeLA) {
            const drainsRes = await fetch(
              `${serverUrl}/bridge/customers/${cid}/liquidation_addresses/${activeLA.id}/drains`,
              { headers: authH },
            );
            if (drainsRes.ok) {
              const drainsData = await drainsRes.json();
              setWithdrawalDrains(drainsData.data || []);
              setHasWithdrawals(true);
              if (!activeTab && !hasDeposits) setActiveTab("withdrawals");
            }
          } else {
            // If no active, but has liquidation addresses → still consider withdrawals enabled
            setHasWithdrawals(true);
          }
        }
      }
    } catch (err) {
      console.error("History loading error:", err);
      toast.error("Failed to load transaction history");
    } finally {
      setLoading(false);
    }
  };

  const refresh = () => {
    if (customerId) {
      loadHistory(customerId);
      toast.success("History refreshed");
    }
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const getEventIcon = (type: string) => {
    if (type.includes("received") || type.includes("funds_received")) {
      return <ArrowDownCircle className="text-green-400" size={20} />;
    }
    return <DollarSign className="text-[#61CE70]" size={20} />;
  };

  const hasAnyHistory = hasDeposits || hasWithdrawals;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#090D14] via-[#0A0E15] to-[#0B0F16] text-white pb-16">
      <Toaster position="top-center" />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 md:pt-12">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
              Transaction History
            </h1>
          </div>

          <button
            onClick={refresh}
            disabled={loading || !customerId}
            className="flex items-center gap-2 px-5 py-2.5 bg-gray-800 hover:bg-gray-700 rounded-xl border border-gray-700 disabled:opacity-50 transition-colors"
          >
            <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>

        {!hasAnyHistory && !loading && (
          <div className="bg-[#0F1419]/70 border border-gray-700/50 rounded-2xl p-10 text-center">
            <History size={48} className="mx-auto text-gray-600 mb-4" />
            <h3 className="text-xl font-medium mb-3">No history yet</h3>
          </div>
        )}

        {hasAnyHistory && (
          <>
            {/* Tabs */}
            <div className="flex border-b border-gray-700 mb-6 overflow-x-auto">
              {hasDeposits && (
                <button
                  onClick={() => setActiveTab("deposits")}
                  className={`flex items-center gap-2 px-6 py-4 font-medium whitespace-nowrap border-b-2 transition-colors ${
                    activeTab === "deposits"
                      ? "border-[#61CE70] text-[#61CE70]"
                      : "border-transparent text-gray-400 hover:text-gray-200"
                  }`}
                >
                  <ArrowDownCircle size={20} />
                  Bank Deposits
                </button>
              )}

              {hasWithdrawals && (
                <button
                  onClick={() => setActiveTab("withdrawals")}
                  className={`flex items-center gap-2 px-6 py-4 font-medium whitespace-nowrap border-b-2 transition-colors ${
                    activeTab === "withdrawals"
                      ? "border-[#61CE70] text-[#61CE70]"
                      : "border-transparent text-gray-400 hover:text-gray-200"
                  }`}
                >
                  <ArrowUpCircle size={20} />
                  Withdrawal Requests
                </button>
              )}
            </div>

            {loading && (
              <div className="flex justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#61CE70]"></div>
              </div>
            )}

            {/* Deposits Content */}
            {activeTab === "deposits" && hasDeposits && !loading && (
              <div className="space-y-5">
                {depositEvents.length === 0 ? (
                  <div className="text-center py-16 bg-[#0F1419]/50 rounded-2xl border border-gray-800">
                    <p className="text-gray-400">No deposits found yet.</p>
                  </div>
                ) : (
                  depositEvents.map((event) => (
                    <div
                      key={event.id}
                      className="bg-[#0F1419]/70 border border-gray-700/50 rounded-2xl p-5 hover:border-gray-500/70 transition-colors"
                    >
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
                        <div className="flex items-start gap-4">
                          <div className="p-3 bg-green-950/40 rounded-xl">
                            {getEventIcon(event.type)}
                          </div>
                          <div>
                            <p className="font-medium capitalize">
                              {event.type.replace(/_/g, " ")}
                            </p>
                            <p className="text-2xl font-bold mt-1">
                              {event.amount} {event.currency.toUpperCase()}
                            </p>
                            <p className="text-sm text-gray-500 mt-1">
                              {formatDate(event.created_at)}
                            </p>
                          </div>
                        </div>
                      </div>

                      {(Number(event.developer_fee_amount) > 0 ||
                        Number(event.exchange_fee_amount) > 0) && (
                        <div className="mt-4 pt-4 border-t border-gray-800 text-sm text-gray-400">
                          <span>Fees: </span>
                          {event.developer_fee_amount !== "0.0" && (
                            <>Dev: {event.developer_fee_amount} </>
                          )}
                          {event.exchange_fee_amount !== "0.0" && (
                            <>Exchange: {event.exchange_fee_amount}</>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Withdrawals Content */}
            {activeTab === "withdrawals" && hasWithdrawals && !loading && (
              <div className="space-y-5">
                {withdrawalDrains.length === 0 ? (
                  <div className="text-center py-16 bg-[#0F1419]/50 rounded-2xl border border-gray-800">
                    <p className="text-gray-400">
                      No withdrawal requests found yet.
                    </p>
                  </div>
                ) : (
                  withdrawalDrains.map((drain) => (
                    <div
                      key={drain.id}
                      className="bg-[#0F1419]/70 border border-gray-700/50 rounded-2xl p-5 hover:border-gray-500/70 transition-colors"
                    >
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-4">
                        <div className="flex items-start gap-4">
                          <div className="p-3 bg-amber-950/40 rounded-xl">
                            <ArrowUpCircle
                              className="text-amber-400"
                              size={24}
                            />
                          </div>
                          <div>
                            <p className="font-medium">Withdrawal Request</p>
                            <p className="text-2xl font-bold mt-1">
                              {drain.amount} {drain.currency.toUpperCase()}
                            </p>
                            <p className="text-sm text-gray-500 mt-1">
                              {formatDate(drain.created_at)}
                            </p>
                          </div>
                        </div>

                        <div className="text-right">
                          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-gray-800">
                            {drain.state === "completed" ? (
                              <>
                                <CheckCircle2
                                  size={16}
                                  className="text-green-400"
                                />
                                Completed
                              </>
                            ) : drain.state === "failed" ? (
                              <>
                                <XCircle size={16} className="text-red-400" />
                                Failed
                              </>
                            ) : (
                              <>
                                <Clock size={16} className="text-yellow-400" />
                                {drain.state}
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-400">You receive:</span>
                          <p className="font-medium">
                            {drain.receipt.converted_amount}{" "}
                            {drain.receipt.destination_currency.toUpperCase()}
                          </p>
                        </div>
                        <div>
                          <span className="text-gray-400">Fee:</span>
                          <p className="font-medium text-red-300">
                            {drain.receipt.developer_fee}
                          </p>
                        </div>

                        {drain.deposit_tx_hash && (
                          <div className="col-span-full">
                            <span className="text-gray-400">Deposit Tx: </span>
                            <a
                              href={`https://basescan.org/tx/${drain.deposit_tx_hash}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#61CE70] hover:underline break-all"
                            >
                              {drain.deposit_tx_hash}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </>
        )}

        {/* Lightning / CashApp Deposits */}
        {isBetaUser(user?.email) && lightningOrders.length > 0 && (
          <div className="mt-8">
            <div className="flex items-center gap-2 mb-4">
              <SiCashapp className="w-4 h-4 text-[#00D632]" />
              <h2 className="text-lg font-semibold text-white">CashApp Deposits</h2>
              <span className="ml-1 px-2 py-0.5 text-[9px] font-bold bg-amber-500/15 text-amber-400 border border-amber-500/25 rounded-full uppercase tracking-wider">Dev</span>
            </div>
            <div className="space-y-3">
              {lightningOrders.map((order) => {
                const statusColor =
                  order.status === "DONE" ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/20" :
                  order.status === "EXPIRED" ? "text-gray-500 bg-gray-500/10 border-gray-500/20" :
                  order.status === "EMERGENCY" ? "text-red-400 bg-red-500/10 border-red-500/20" :
                  "text-amber-400 bg-amber-500/10 border-amber-500/20";
                const statusLabel =
                  order.status === "DONE" ? "Completed" :
                  order.status === "EXPIRED" ? "Expired" :
                  order.status === "EMERGENCY" ? "Issue" :
                  order.status === "EXCHANGE" ? "Processing" :
                  order.status === "PENDING" ? "Pending" : "Waiting";
                const date = order.createdAt?.seconds
                  ? new Date(order.createdAt.seconds * 1000).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
                  : "—";
                return (
                  <div key={order.id} className="bg-[#0F1419]/70 border border-gray-700/50 rounded-xl p-4 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-[#00D632]/10 flex items-center justify-center shrink-0">
                        <SiCashapp className="w-4 h-4 text-[#00D632]" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-white">${order.usdAmount} USDC</p>
                        <p className="text-xs text-gray-500 font-mono">{order.btcAmount} BTC · {date}</p>
                      </div>
                    </div>
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${statusColor}`}>
                      {statusLabel}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
